import {
  Column,
  ColumnDef,
  SortingState,
  Table,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import moment from 'moment';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  OrdersOverviewResponse,
  OrdersOverviewGroupData,
  useGetOrdersOverview,
} from '@frontend/api';
import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  TableContainer,
  TableHeaderCellContentPrefab,
  TableHeaderCellContentPrefabProps,
  TableV2,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
  UserGroupBadge,
} from '@frontend/legacy-components';

import {
  DefaultTableShadowOverlay,
  StickyElementRefs,
} from 'components/listTablePage';

import { formatPrice } from 'utils/formatters';
import { useUserGroupCollectionContext } from 'utils/multisite';
import { pluralize, toSnakeCase } from 'utils/stringUtils';

import {
  Pane,
  PaneHeader,
  PaneTitle,
  PaneBody,
  PaneNoAccessState,
  PaneSubtitle,
  createPaneAccessAwareFallback,
  isNoAccessError,
} from '../components/pane';
import { MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS } from '../multisiteDashboardDataRefresh';

const EMPTY_STICKY_REFS: StickyElementRefs = {};
const ORDERS_OVERVIEW_FALLBACK = createPaneAccessAwareFallback('orders');

type CountCellContentProps = {
  count: OrdersOverviewGroupData['count'];
  userGroupId?: number;
};

const CountCellContent = ({ count, userGroupId }: CountCellContentProps) => {
  const { initChangeSite } = useUserGroupCollectionContext();

  const handleCountClick = useCallback(
    (userGroupId: number) => {
      const start = moment().subtract(30, 'days').startOf('day').format();
      const end = moment().endOf('day').format();
      const orderedRange = encodeURIComponent(`${start},${end}`);
      initChangeSite({
        nextUrl: `/orders/all?ordered_range=${orderedRange}`,
        userGroupId: userGroupId,
      });
    },
    [initChangeSite],
  );

  const onClick =
    count > 0 && userGroupId ? () => handleCountClick(userGroupId) : undefined;

  return (
    <Box textAlign="end">
      {count === 0 ? (
        <Box color="feature.placeholder">--</Box>
      ) : onClick ? (
        <Button variant="link" size="md" onClick={onClick}>
          {pluralize('order', count)}
        </Button>
      ) : (
        <Box>{pluralize('order', count)}</Box>
      )}
    </Box>
  );
};

type PriceCellContentProps = {
  totalPrice: OrdersOverviewGroupData['totalPrice'];
};

const PriceCellContent = ({ totalPrice }: PriceCellContentProps) => {
  return (
    <Box textAlign="end">
      {totalPrice ? (
        <Box fontFamily="data">{formatPrice(totalPrice)}</Box>
      ) : (
        <Box color="feature.placeholder">--</Box>
      )}
    </Box>
  );
};

const TableHeaderCellContent = ({
  column,
  table,
  ...restProps
}: {
  column: Column<OrdersOverviewGroupData>;
  table: Table<OrdersOverviewGroupData>;
} & Pick<TableHeaderCellContentPrefabProps, 'columnTitle'> &
  Partial<Omit<TableHeaderCellContentPrefabProps, 'columnTitle'>>) => {
  const sorting = table.getState().sorting;
  const current = sorting.find((s) => s.id === column.id);
  return (
    <TableHeaderCellContentPrefab
      columnDataIndex={column.id}
      sortPossibleDirections={['ascending', 'descending']}
      sortDirection={
        current ? (current.desc ? 'descending' : 'ascending') : undefined
      }
      onIncrementSort={() =>
        table.setSorting([{ id: column.id, desc: !current?.desc }])
      }
      {...restProps}
    />
  );
};

const EMPTY_ORDERS_TABLE_GROUPS: OrdersOverviewGroupData[] = [];

const DEFAULT_TOTAL_DATA = {
  count: 0,
  totalPrice: null,
} as const satisfies OrdersOverviewResponse['total'];

const TotalDataContext =
  createContext<OrdersOverviewResponse['total']>(DEFAULT_TOTAL_DATA);

const useTotalData = () => useContext(TotalDataContext);

const columns: ColumnDef<OrdersOverviewGroupData>[] = [
  {
    id: 'userGroup',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Site Name"
        column={column}
        table={table}
      />
    ),
    cell: ({ row }) => {
      const { name, icon, color } = row.original.userGroup;
      return (
        <UserGroupBadge
          name={name}
          icon={icon}
          colorPalette={color}
          size="sm"
          maxWidth="max-content"
        />
      );
    },
    footer: () => (
      <Flex height="full" alignItems="center">
        Total
      </Flex>
    ),
  },
  {
    id: 'count',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="# of Orders"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <CountCellContent
        count={row.original.count}
        userGroupId={row.original.userGroup.id}
      />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <CountCellContent count={totalData.count} />;
    },
  },
  {
    id: 'totalPrice',
    header: ({ column, table }) => (
      <TableHeaderCellContent
        columnTitle="Total Value"
        column={column}
        table={table}
        headerButtonProps={{ justifyContent: 'flex-end' }}
      />
    ),
    cell: ({ row }) => (
      <PriceCellContent totalPrice={row.original.totalPrice} />
    ),
    footer: function Footer() {
      const totalData = useTotalData();
      return <PriceCellContent totalPrice={totalData.totalPrice} />;
    },
  },
];

type OrdersTableProps = {
  /** Optional preloaded data (e.g. from stories); live data comes from the hook. */
  data?: OrdersOverviewResponse;
  activeGroupIds: number[];
};

const subgridSx = {
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridColumn: '1 / -1',
} as const;

const OrdersTable = ({ data: dataProp, activeGroupIds }: OrdersTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'userGroup', desc: false },
  ]);

  const query = useMemo(
    () => ({
      o: `${sorting
        .map((s) => `${s.desc ? '-' : ''}${toSnakeCase(s.id)}`)
        .join(',')}`,
      ...(activeGroupIds.length > 0 ? { groups: activeGroupIds } : {}),
    }),
    [sorting, activeGroupIds],
  );

  const ordersQuery = useGetOrdersOverview({
    query,
    queryOptions: {
      // The 'no_access' 403 is handled inline (below) instead of thrown to
      // the pane's error boundary; any other error still throws as before.
      useErrorBoundary: (error) => !isNoAccessError(error),
      ...MULTISITE_DASHBOARD_POLL_QUERY_OPTIONS,
    },
  });

  const data = ordersQuery.data ?? dataProp;

  const table = useReactTable<OrdersOverviewGroupData>({
    data: data?.groups ?? EMPTY_ORDERS_TABLE_GROUPS,
    columns,
    getRowId: (row) => row.userGroup.id.toString(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  const { rows } = table.getRowModel();

  const headerGroups = table.getHeaderGroups();

  const scrollRef = useRef<HTMLDivElement>(null);

  if (isNoAccessError(ordersQuery.error)) {
    return <PaneNoAccessState resourceLabel="orders" />;
  }

  return (
    <TableContainer
      border="none"
      borderRadius="none"
      position="relative"
      variant="simple"
      minHeight="full"
    >
      {ordersQuery.isFetching ? (
        <Center
          as="span"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex="overlay"
          bg="page.background"
          opacity={0.5}
        >
          <Spinner size="xl" />
        </Center>
      ) : null}
      <DefaultTableShadowOverlay
        overflowBoxRef={scrollRef}
        stickyElementRefs={EMPTY_STICKY_REFS}
      />
      <Box w="100%" flexGrow={1} overflow="auto" ref={scrollRef}>
        <TableV2
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) auto auto',
            gridTemplateRows: 'auto auto minmax(0, 1fr) auto', // one "row" per sub grid
            minHeight: '100%',
            '--row-hover-bg': 'var(--chakra-colors-page-background)',
            '--row-active-bg': 'var(--chakra-colors-page-background)',
          }}
        >
          <Thead sx={subgridSx}>
            {headerGroups.map((headerGroup) => (
              <Tr key={headerGroup.id} sx={subgridSx}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    textAlign="right"
                    padding="0 !important" // to support the prefab which has hardcoded padding
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody sx={subgridSx}>
            {rows.map((row) => (
              <Tr key={row.id} sx={subgridSx} isInteractive cursor="default">
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    py={0}
                    height={5}
                    fontWeight="medium"
                    outerProps={{
                      padding: 0,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
          <Tfoot sx={subgridSx} />
          <TotalDataContext.Provider value={data?.total ?? DEFAULT_TOTAL_DATA}>
            <Tfoot sx={subgridSx}>
              {table.getFooterGroups().map((footerGroup) => (
                <Tr key={footerGroup.id} sx={subgridSx}>
                  {footerGroup.headers.map((header) => (
                    <Th key={header.id} border="none">
                      {flexRender(
                        header.column.columnDef.footer,
                        header.getContext(),
                      )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Tfoot>
          </TotalDataContext.Provider>
        </TableV2>
      </Box>
    </TableContainer>
  );
};

export type OrdersPaneProps = {
  activeGroupIds: number[];
};

export const OrdersPane = ({ activeGroupIds }: OrdersPaneProps) => {
  const websocketOptions = useMemo(() => {
    return {
      topic: activeGroupIds.map((id) => `${id}.orders.created`),
    };
  }, [activeGroupIds]);

  return (
    <Pane refetchQueryKey="" websocketOptions={websocketOptions}>
      <PaneHeader>
        <PaneTitle>New Orders</PaneTitle>
        <PaneSubtitle>Placed in the last 30 days</PaneSubtitle>
      </PaneHeader>
      <PaneBody fallback={ORDERS_OVERVIEW_FALLBACK}>
        <OrdersTable activeGroupIds={activeGroupIds} />
      </PaneBody>
    </Pane>
  );
};
